import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown, Menu, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { storage } from '@/lib/storage';
import { updateUserData } from '@/lib/store/slices/UserSlice';
import { RootState } from '@/lib/store';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { setRefreshTokenCookie } from '@/lib/clientCookie';
import toast from 'react-hot-toast';
import { logoutService } from '@/services/auth';
import { clearPermissions } from '@/lib/store/slices/PermissionsSlice';

const NAV = [
  { label: 'Dashboard',         path: '/telecaller-dashboard', screen: null },
  { label: 'Subscription Data', path: '/subscription-data',    screen: 'subscription data' },
  { label: 'Tele Record',       path: '/tele-record',          screen: 'telerecords' },
];

export default function TelecallerHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useCurrentUser();
  const [showUser, setShowUser] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { loaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  let isLoggingOut = false;

  const canSee = (screen: string | null) =>
    !screen || !loaded || (screen in permissions && permissions[screen]?.Can_View !== false);

  const visibleNav = NAV.filter(item => canSee(item.screen));

  useEffect(() => { setShowMobileMenu(false); setShowUser(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!(target as Element)?.closest?.('[data-user-menu]')) setShowUser(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
      navigate("/login", { replace: true });

      isLoggingOut = false;
    }
  };

  const role = (userDetails?.role as string | undefined) ?? '';
  const username = userDetails?.username as string | undefined;

  return (
    <header className="sticky top-0 z-40" style={{ background: 'linear-gradient(135deg, #1e2530 0%, #2d3748 50%, #1e2530 100%)', boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05)' }}>

      {/* Top accent line */}
      <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #d4a017, #f0c040, #d4a017, transparent)' }} />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">

        {/* Logo */}
        <Link to="/telecaller-dashboard" className="flex items-center gap-2.5 flex-shrink-0 group">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl overflow-hidden border border-mlb-gold/30 group-hover:border-mlb-gold/60 transition-colors" style={{ boxShadow: '0 0 12px rgba(212,160,23,0.15)' }}>
            <img src="/mlblogo.jpeg" alt="MLB" className="w-full h-full object-cover" />
          </div>
          <div className="xs:block lg:hidden xl:block">
            <h1 className="text-base sm:text-lg font-extrabold text-white tracking-tight leading-tight">Mera Lunch Box</h1>
            <p className="text-mlb-gold text-[9px] font-semibold tracking-widest uppercase leading-none">Taaza Khana, Ghar Ka Khana</p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-0.5">
          {visibleNav.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              className={`text-sm font-semibold transition-all px-3 py-2 rounded-lg whitespace-nowrap ${
                pathname === path
                  ? 'text-[#1e2530] bg-mlb-gold shadow-md'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* User menu — desktop */}
          <div className="relative hidden sm:block" data-user-menu>
            <button
              onClick={() => setShowUser(p => !p)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 bg-mlb-gold rounded-full flex items-center justify-center text-sm font-bold text-[#1e2530] flex-shrink-0">
                {username?.[0]?.toUpperCase()}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm text-white font-semibold leading-tight">{username}</p>
                {role && <p className="text-[10px] text-mlb-gold leading-tight capitalize">{role}</p>}
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-white/50" />
            </button>

            {showUser && (
              <div className="absolute right-0 mt-2 w-52 rounded-xl overflow-hidden z-50" style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                <div className="p-4 border-b border-white/8">
                  <p className="text-sm font-bold text-white">{username}</p>
                  <p className="text-xs text-white/50 truncate mt-0.5">{(userDetails as any)?.email}</p>
                  {role && <span className="mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-mlb-gold text-[#1e2530] capitalize">{role}</span>}
                </div>
                <div className="p-2">
                  <button type='button' onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/8 hover:text-red-300 rounded-lg transition-colors">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setShowMobileMenu(prev => !prev)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
          >
            {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden absolute top-full left-0 right-0 z-50 border-t border-white/8 shadow-2xl max-h-[calc(100vh-60px)] overflow-y-auto" style={{ background: '#1a2030' }}>
          <div className="max-w-screen-2xl mx-auto px-4 py-3 space-y-1">

            {/* User info */}
            <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="w-10 h-10 bg-mlb-gold rounded-full flex items-center justify-center text-base font-bold text-[#1e2530] flex-shrink-0">
                {username?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{username}</p>
                {role && <p className="text-xs text-mlb-gold capitalize">{role}</p>}
              </div>
              <button type='button' onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-white/10 transition-colors">
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>

            {visibleNav.map(({ label, path }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  pathname === path
                    ? 'bg-mlb-gold text-[#1e2530]'
                    : 'text-white/75 hover:text-white hover:bg-white/8'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
